import { useState } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const earlyMonthly = (base) => base;
const earlyYearlyTotal = (base) => +(base * 12 * 0.85).toFixed(2);
const earlyYearlyMonthly = (base) => +(base * 0.85).toFixed(2);

const PLANS = [
  {
    key: "beginner",
    nameKey: "Plans.Package.Features.Name",
    regularPrice: 49,
    earlyPrice: 9.99,
    features: [
      { key: "Plans.Package.Features.Features.Creation", included: true },
      { key: "Plans.Package.Features.Features.Schedule", included: true },
      { key: "Plans.Package.Features.Features.AI", included: true },
      { key: "Plans.Package.Features.Features.Analytics", included: true },
      { key: "Plans.Package.Features.Features.Calender", included: true },
      { key: "Plans.Package.Features.Features.TeamFunctions", included: false },
      { key: "Plans.Package.Features.Features.AdvancedAnalytics", included: false },
      { key: "Plans.Package.Features.Features.FreeSetup", included: false },
    ],
  },
  {
    key: "professional",
    nameKey: "Plans.Package.Professional.Name",
    regularPrice: 99,
    earlyPrice: 19.99,
    highlighted: true,
    features: [
      { key: "Plans.Package.Features.Features.Creation", included: true },
      { key: "Plans.Package.Features.Features.Schedule", included: true },
      { key: "Plans.Package.Features.Features.AI", included: true },
      { key: "Plans.Package.Features.Features.Analytics", included: true },
      { key: "Plans.Package.Features.Features.Calender", included: true },
      { key: "Plans.Package.Features.Features.TeamFunctions", included: true },
      { key: "Plans.Package.Features.Features.AdvancedAnalytics", included: true },
      { key: "Plans.Package.Features.Features.FreeSetup", included: false },
    ],
  },
  {
    key: "growth",
    nameKey: "Plans.Package.Growth.Name",
    regularPrice: 199,
    earlyPrice: 29.99,
    features: [
      { key: "Plans.Package.Features.Features.Creation", included: true },
      { key: "Plans.Package.Features.Features.Schedule", included: true },
      { key: "Plans.Package.Features.Features.AI", included: true },
      { key: "Plans.Package.Features.Features.Analytics", included: true },
      { key: "Plans.Package.Features.Features.Calender", included: true },
      { key: "Plans.Package.Features.Features.TeamFunctions", included: true },
      { key: "Plans.Package.Features.Features.AdvancedAnalytics", included: true },
      { key: "Plans.Package.Features.Features.FreeSetup", included: true },
    ],
  },
];

const PricingSection = ({ paymentId = null, stripeUrl = null, ctaHref = null }) => {
  const { t } = useTranslation();
  const [yearly, setYearly] = useState(false);

  const navigate = useNavigate();
  const getPlanPrice = (plan) => yearly ? earlyYearlyMonthly(plan.earlyPrice) : earlyMonthly(plan.earlyPrice);
  const getPlanYearlyTotal = (plan) => earlyYearlyTotal(plan.earlyPrice);
  const isInternal = ctaHref?.startsWith("/");
  const checkoutUrl = ctaHref
    ?? (stripeUrl
      ? `${stripeUrl}${paymentId ? `?client_reference_id=${paymentId}` : ""}`
      : null);

  return (
    <>
      {/* Header */}
      <Row className="justify-content-center mb-4">
        <Col lg={6} className="text-center">
          <h4>{t("Plans.Title")}</h4>
          <p className="text-muted">{t("Plans.MotivationQuote")}</p>
        </Col>
      </Row>

      {/* Toggle */}
      <Row className="justify-content-center mb-5">
        <Col xs="auto">
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            background: "#f5f7fa", borderRadius: 50,
            padding: "6px 8px", border: "1px solid #eaecf0",
          }}>
            <button
              type="button"
              onClick={() => setYearly(false)}
              style={{
                padding: "7px 20px", borderRadius: 50, border: "none",
                background: !yearly ? "#fff" : "transparent",
                boxShadow: !yearly ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                fontWeight: 600, fontSize: 13,
                color: !yearly ? "#1a1f2e" : "#9299a8",
                cursor: "pointer", transition: "all 0.18s ease",
              }}
            >
              {t("Plans.Toggle.Monthly")}
            </button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              style={{
                padding: "7px 20px", borderRadius: 50, border: "none",
                background: yearly ? "#fff" : "transparent",
                boxShadow: yearly ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                fontWeight: 600, fontSize: 13,
                color: yearly ? "#1a1f2e" : "#9299a8",
                cursor: "pointer", transition: "all 0.18s ease",
                display: "flex", alignItems: "center", gap: 7,
              }}
            >
              {t("Plans.Toggle.Yearly")}
              <span style={{
                background: "linear-gradient(135deg, #51d28c, #2ecc71)",
                color: "#fff", fontSize: 10, fontWeight: 700,
                borderRadius: 20, padding: "2px 7px",
              }}>
                {t("Plans.Toggle.SaveBadge")}
              </span>
            </button>
          </div>
        </Col>
      </Row>

      {/* Cards */}
      <Row className="justify-content-center">
        <Col lg={11}>
          <Row className="justify-content-center align-items-start">
            {PLANS.map((plan) => {
              const h = plan.highlighted;
              return (
                <Col xl={4} md={6} key={plan.key}
                  style={{ marginTop: h ? 0 : 28 }}
                  className="mb-4"
                >
                  <Card
                    className="pricing-box"
                    style={{
                      borderRadius: h ? 20 : 16,
                      overflow: "hidden",
                      border: h ? "2px solid #564ab1" : "1px solid #eaecf0",
                      boxShadow: h
                        ? "0 20px 60px rgba(86,74,177,0.28)"
                        : "0 2px 12px rgba(0,0,0,0.06)",
                      position: "relative",
                      zIndex: h ? 2 : 1,
                    }}
                  >
                    {/* Most popular banner */}
                    <div style={{
                      background: h
                        ? "linear-gradient(135deg, #564ab1, #3d3490)"
                        : "transparent",
                      color: "#fff",
                      textAlign: "center",
                      fontSize: 11, fontWeight: 700,
                      padding: h ? "8px 0" : "0",
                      letterSpacing: 0.8, textTransform: "uppercase",
                      height: h ? "auto" : 0,
                      overflow: "hidden",
                    }}>
                      {h ? t("Plans.MostPopular") : ""}
                    </div>

                    <Card.Body style={{ padding: h ? "32px 28px" : "24px 20px" }}>

                      {/* Plan name */}
                      <h5 style={{
                        fontWeight: 700, marginBottom: 14,
                        color: "#1a1f2e", fontSize: h ? 18 : 15,
                      }}>
                        {t(plan.nameKey)}
                      </h5>

                      {/* Early access badge */}
                      <div style={{ marginBottom: 10 }}>
                        <span style={{
                          background: "linear-gradient(135deg, #f59e0b, #f97316)",
                          color: "#fff", fontSize: 10, fontWeight: 700,
                          borderRadius: 20, padding: "3px 10px",
                          letterSpacing: 0.6, textTransform: "uppercase",
                        }}>
                          {t("Plans.EarlyAccess.Badge")}
                        </span>
                      </div>

                      {/* Prices */}
                      <div style={{ marginBottom: 20 }}>
                        <div style={{
                          fontSize: 14, color: "#9299a8",
                          textDecoration: "line-through", marginBottom: 4,
                        }}>
                          €{plan.regularPrice}/{t("Plans.Timeframe.Monthly")}
                        </div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                          <span style={{ fontSize: h ? 16 : 13, color: "#374151", fontWeight: 500 }}>€</span>
                          <span style={{
                            fontSize: h ? 52 : 36, fontWeight: 800,
                            color: h ? "#564ab1" : "#1a1f2e",
                            lineHeight: 1,
                          }}>
                            {getPlanPrice(plan).toFixed(2).replace(".", ",")}
                          </span>
                          <span style={{ fontSize: h ? 14 : 13, color: "#9299a8" }}>
                            /{t("Plans.Timeframe.Monthly")}
                          </span>
                        </div>
                        {yearly && (
                          <div style={{ fontSize: 12, color: "#7c6fc4", marginTop: 4, fontWeight: 500 }}>
                            {t("Plans.EarlyAccess.BilledYearly", { price: getPlanYearlyTotal(plan).toFixed(2).replace(".", ",") })}
                          </div>
                        )}
                      </div>

                      {/* Free trial */}
                      <div style={{
                        display: "flex", alignItems: "center", gap: 7,
                        background: "rgba(81,210,140,0.08)",
                        border: "1px solid rgba(81,210,140,0.3)",
                        borderRadius: 8, padding: "8px 12px", marginBottom: 16,
                      }}>
                        <i className="uil uil-gift text-success font-size-16" style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1a7a4a" }}>
                          {t("Plans.FreeTrial")}
                        </span>
                      </div>

                      {/* CTA */}
                      <Button
                        href={isInternal ? undefined : (checkoutUrl ?? "#")}
                        onClick={isInternal ? () => navigate(checkoutUrl) : undefined}
                        className="w-100"
                        target={!isInternal && checkoutUrl ? "_blank" : undefined}
                        style={{
                          background: h
                            ? "linear-gradient(135deg, #564ab1, #3d3490)"
                            : undefined,
                          border: h ? "none" : undefined,
                          borderRadius: 8, fontWeight: 600,
                          padding: h ? "12px 0" : "10px 0",
                          fontSize: h ? 15 : 13,
                        }}
                      >
                        {t("Plans.Buttons.GetStarted")}
                      </Button>

                      <hr style={{ margin: "20px 0", borderColor: "#f3f4f6" }} />

                      {/* Features */}
                      <ul className="list-unstyled mb-0" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {plan.features.map((f) => (
                          <li key={f.key} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: h ? 14 : 13 }}>
                            <i
                              className={`uil ${f.included ? "uil-check-circle text-success" : "uil-times-circle text-danger"} font-size-16`}
                              style={{ flexShrink: 0, marginTop: 1 }}
                            />
                            <span style={{ color: f.included ? "#374151" : "#9299a8" }}>
                              {t(f.key)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default PricingSection;
